// src/features/users/components/UserCreate.tsx

import React from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  PasswordInput,
  SelectInput,
  ReferenceInput,
  useNotify,
  useRedirect,
  required,
  useTranslate,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box } from '@mui/material';
import { 
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';

import { MyToolbar } from '../../../components/forms/MyToolbar';
import { FormContainer } from '../../../components/forms/MyFormContainer';
import { 
  TwoColumnLayout,
  MyFormSection 
} from '../../../components/forms/MyFormLayout';

import { useUserValidation } from '../hooks/useUserValidation';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { prepareUserForAPI } from '../types';

const RoleBasedAssignment = () => {
    const { watch } = useFormContext();
    const translate = useTranslate();
    const role = watch('role');
    const { currentUserRole } = useUserPermissions();

    if (role === 'HotelAdmin' || role === 'Receptionist') {
        return (
            <ReferenceInput 
                source="hotelId" 
                reference="hotels" 
                label={translate('users.fields.hotelId')} 
                fullWidth
            >
                <SelectInput 
                    optionText="name" 
                    validate={required()} 
                />
            </ReferenceInput>
        );
    }

    if (role === 'GroupAdmin' && currentUserRole === 'SuperAdmin') {
        return (
            <ReferenceInput 
                source="groupId" 
                reference="groups" 
                label={translate('users.fields.groupId')} 
                fullWidth
            >
                <SelectInput 
                    optionText="name" 
                    validate={required()} 
                />
            </ReferenceInput>
        );
    }
    
    return null;
};

export const UserCreate: React.FC = () => {
    const notify = useNotify();
    const redirect = useRedirect();
    const translate = useTranslate();
    
    const { validateName, validateEmail, validatePassword, validateForm } = useUserValidation();
    const { currentUserRole } = useUserPermissions();

    const roleChoices = [
        { id: 'Receptionist', name: translate('users.roles.Receptionist') },
        { id: 'HotelAdmin', name: translate('users.roles.HotelAdmin') },
    ];
    
    if (currentUserRole === 'SuperAdmin' || currentUserRole === 'GroupAdmin') {
        roleChoices.push({ id: 'GroupAdmin', name: translate('users.roles.GroupAdmin') });

        if (currentUserRole === 'SuperAdmin') {
            roleChoices.push({ id: 'SuperAdmin', name: translate('users.roles.SuperAdmin') });
        }
    }

    const validateUserForm = (values: any) => {
        return validateForm(values, true);
    };

    const handleCancel = () => redirect('/users');
    
    return (
        <Create
            title={translate('users.titles.create')}
            transform={prepareUserForAPI}
            mutationOptions={{
                onSuccess: () => {
                    notify(translate('users.messages.create_success'), { type: 'success' });
                    redirect('/users');
                },
            }}
            redirect={false}
        >
            <FormContainer maxWidth="lg">
                <SimpleForm
                    toolbar={
                        <MyToolbar 
                            onCancel={handleCancel} 
                            resource="users" 
                            showDelete={false} 
                        />
                    }
                    validate={validateUserForm}
                >
                    <TwoColumnLayout>
                        <MyFormSection
                            title={translate('users.titles.user_information')}
                            subtitle={translate('users.help.basic_new_user_data')}
                            icon={<PersonAddIcon />}
                            variant="outlined"
                        >
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: 3 
                            }}>
                                <TextInput 
                                    source="name" 
                                    label={translate('users.fields.name')} 
                                    validate={[required(), validateName]} 
                                    fullWidth 
                                />
                                <TextInput 
                                    source="email" 
                                    label={translate('users.fields.email')} 
                                    validate={[required(), validateEmail]} 
                                    fullWidth 
                                />
                                <PasswordInput 
                                    source="password" 
                                    label={translate('users.fields.password')} 
                                    validate={[required(), validatePassword]} 
                                    fullWidth 
                                />
                            </Box>
                        </MyFormSection>
                        <MyFormSection
                            title={translate('users.titles.role_assignment')}
                            subtitle={translate('users.help.role_selection')}
                            icon={<AdminIcon />}
                            variant="outlined"
                        >
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: 3 
                            }}>
                                <SelectInput 
                                    source="role" 
                                    label={translate('users.fields.role')} 
                                    choices={roleChoices} 
                                    validate={required()} 
                                    fullWidth 
                                />
                                <RoleBasedAssignment />
                            </Box>
                        </MyFormSection>
                    </TwoColumnLayout>
                </SimpleForm>
            </FormContainer>
        </Create>
    );
};

export default UserCreate;